import React from "react";
import { useSearchParams } from "react-router-dom";
import { DEFAULT_PER_PAGE, fetchReportBodyAsync, fetchReportHeaderAsync } from "@core/reports/api/fetchReportPage";
import { activeReportTab, layoutParamCorrection, useReportLayoutParam, withLayout } from "@core/reports/hooks/useReportLayoutParam";
import { lensKeyForTab, originForTab, queryKeysForTab, reportTabsForScope } from "@core/reports/utils/reportSectionOrigin";
import { REPORT_SPA_QUERY_PARAMS } from "@core/reports/constants/reportSpaQueryParams";
import { LAYOUT_DEFAULT } from "@features/presentationLayout/types/presentationLayout";
import {
  fetchDepartmentReportsQueryAsync,
  type DepartmentReportListItem,
} from "@core/reports/api/fetchDepartmentReportsQuery";
import { requestReportApproveAsync } from "@core/reports/api/requestReportApprove";
import { requestReportReviewAsync } from "@core/reports/api/requestReportReview";
import { requestDepartmentReportSaveAsync } from "@core/reports/api/requestDepartmentReportSave";
import { requestDepartmentReportSubmitAsync } from "@core/reports/api/requestDepartmentReportSubmit";
import { processDepartmentReportAsync } from "@core/reports/api/processDepartmentReport";
import type {
  PaginatedReportResponse,
  ReportHeaderPayload,
  ReportProcessResult,
  DepartmentReportMediaItem,
  DepartmentReportDocument,
  SubmissionSummary,
} from "@core/reports/api/types";
import { toTreeColumnsSpec, buildTreeByScope, buildTreeForSection } from "@core/reports/adapters/reportTreeAdapters";
import { useReportTags } from "@features/report/tag/hooks/useReportTags";
import { useDepartmentReportMedia } from "@core/reports/hooks/useDepartmentReportMedia";
import { getPaginationRange } from "@core/components/TablePaginationFooter";
import {
  canApproveDepartmentReport,
  canValidateDepartmentReport,
  getApprovalNotes,
  getNeedsAttentionReason,
  parseNeedsAttentionDetails,
  isCurrentUserSubmitter,
  isDepartmentReportEditable,
  isDepartmentReportMissing,
  isTagClosed,
  isTagOpen,
  normalizeReportWorkflowStatus,
  resolveDepartmentReportDisplayStatus,
  type ReportProcessResults,
} from "@core/reports/utils/reportWorkflow";
import { useReportActionPermissions } from "@core/reports/hooks/useReportActionPermissions";
import {
  buildReportPermissionFlags,
  type ReportPermissionFlags,
} from "@core/reports/utils/reportPermissionFlags";
import { surfaceDeptActionError } from "@core/reports/utils/surfaceDeptActionError";
import { useAuthStore } from "@features/auth/store/authStore";
import {
  collectLensApiParams,
  parseReportSectionView,
  readActiveReportSection,
  writeActiveReportSection,
  writeSectionLens,
  type ReportSectionViewLensParams,
  type SectionLensKey,
} from "@core/reports/utils/reportSectionViewLens";

export type ReportScope = "church" | "district" | "nation";

/**
 * Maps a scope + entity id to the discriminated `{ scope, <idField> }` part the report APIs expect.
 */
function scopeIdPart(scope: ReportScope, entityId: number) {
  switch (scope) {
    case "church":
      return { scope, churchId: entityId } as const;
    case "district":
      return { scope, districtId: entityId } as const;
    default:
      return { scope, nationId: entityId } as const;
  }
}

/** Replaces the controller in `ref` with a new one, aborting the request it belonged to. */
function nextAbortController(ref: React.MutableRefObject<AbortController | null>): AbortController {
  ref.current?.abort();
  ref.current = new AbortController();
  return ref.current;
}

/** Everything the active-tab body request depends on. Switching tabs changes the key and refetches. */
export function reportBodyKey(reportKey: string, params: URLSearchParams, scope: ReportScope = "church"): string {
  const tab = activeReportTab(scope, params);
  const keys = queryKeysForTab(scope, tab);
  const layout = params.get(keys.layout);
  return JSON.stringify([
    reportKey,
    tab,
    layout && layout !== LAYOUT_DEFAULT ? layout : null,
    params.get(keys.page),
    params.get(REPORT_SPA_QUERY_PARAMS.PER_PAGE),
    collectLensApiParams(params, lensKeyForTab(scope, tab), scope),
  ]);
}

export interface UseReportPageConfig {
  scope: ReportScope;
  entityId: number;
  departmentId: number;
  reportTagId: number;
}

export function useReportPage(config: UseReportPageConfig) {
  const { scope, entityId, departmentId, reportTagId } = config;

  const [searchParams, setSearchParams] = useSearchParams();
  const perPage = Number(searchParams.get(REPORT_SPA_QUERY_PARAMS.PER_PAGE)) || DEFAULT_PER_PAGE;
  const activeTab = activeReportTab(scope, searchParams);
  const activeKeys = queryKeysForTab(scope, activeTab);
  const page = Number(searchParams.get(activeKeys.page)) || 1;

  const [header, setHeader] = React.useState<ReportHeaderPayload | null>(null);
  const [headerLoading, setHeaderLoading] = React.useState(false);
  const [headerError, setHeaderError] = React.useState<string | null>(null);
  const [resp, setResp] = React.useState<PaginatedReportResponse | null>(null);
  const [bodyLoading, setBodyLoading] = React.useState(false);
  const [bodyError, setBodyError] = React.useState<string | null>(null);
  const loading = headerLoading || bodyLoading;
  /** Actions only depend on the report details and their own requests, so a table or layout reload never disables them. */
  const actionsLoading = headerLoading;
  const error = headerError ?? bodyError;

  const [deptReport, setDeptReport] = React.useState<DepartmentReportListItem | null>(null);
  const [deptReportLoading, setDeptReportLoading] = React.useState(false);

  // Department-report action state
  const [deptNotes, setDeptNotes] = React.useState("");
  const [savingDraft, setSavingDraft] = React.useState(false);
  const [submittingDept, setSubmittingDept] = React.useState(false);
  const [validatingDept, setValidatingDept] = React.useState(false);
  const [deptActionError, setDeptActionError] = React.useState<string | null>(null);
  const [validationResult, setValidationResult] = React.useState<ReportProcessResult | null>(null);

  // Approve modal
  const [showApproveModal, setShowApproveModal] = React.useState(false);
  const [approveNotes, setApproveNotes] = React.useState("");
  const [approving, setApproving] = React.useState(false);
  const [approveError, setApproveError] = React.useState<string | null>(null);

  // Request review modal
  const [showReviewModal, setShowReviewModal] = React.useState(false);
  const [requestingReview, setRequestingReview] = React.useState(false);
  const [reviewError, setReviewError] = React.useState<string | null>(null);

  const { data: tags = [], loading: tagsLoading } = useReportTags({
    ...(scope === "church"
      ? { churchId: entityId || undefined }
      : scope === "district"
        ? { districtId: entityId || undefined }
        : { nationId: entityId || undefined }),
    all: true,
  });

  const canLoad = entityId > 0 && departmentId > 0 && reportTagId > 0;
  const { requestedLayout, setLayout, correcting } = useReportLayoutParam(canLoad, scope);
  const reportKey = `${scope}:${entityId}:${departmentId}:${reportTagId}`;
  const bodyKey = reportBodyKey(reportKey, searchParams, scope);
  /** Body already loaded for a URL the page corrected itself, so it is not fetched again. */
  const preloadedBodyKeyRef = React.useRef<string | null>(null);
  /** Set when the next header load should replace the notes box: a save, or a different report. */
  const syncNotesRef = React.useRef(true);
  const latestHeaderRequestRef = React.useRef(0);
  const latestBodyRequestRef = React.useRef(0);
  const headerAbortRef = React.useRef<AbortController | null>(null);
  const bodyAbortRef = React.useRef<AbortController | null>(null);

  React.useEffect(
    () => () => {
      headerAbortRef.current?.abort();
      bodyAbortRef.current?.abort();
    },
    []
  );

  const onPageChange = React.useCallback(
    (nextPage: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(queryKeysForTab(scope, activeReportTab(scope, next)).page, String(nextPage));
        return next;
      });
    },
    [scope, setSearchParams]
  );

  const loadHeader = React.useCallback(async (): Promise<void> => {
    if (!canLoad) return;
    const requestId = ++latestHeaderRequestRef.current;
    const { signal } = nextAbortController(headerAbortRef);
    const isCurrent = () => !signal.aborted && requestId === latestHeaderRequestRef.current;
    setHeaderLoading(true);
    setHeaderError(null);
    try {
      const result = await fetchReportHeaderAsync(
        {
          ...scopeIdPart(scope, entityId),
          departmentId,
          reportTagId,
        } as Parameters<typeof fetchReportHeaderAsync>[0],
        signal
      );
      if (!isCurrent()) return;
      if (result.isSuccess()) {
        const next = result.getData()?.data ?? null;
        setHeader(next);
        if (syncNotesRef.current) {
          syncNotesRef.current = false;
          setDeptNotes(String(next?.meta?.department_submission?.notes ?? ""));
        }
      } else {
        syncNotesRef.current = false;
        setHeaderError(result.getError()?.detail || "Failed to fetch report");
      }
    } catch (e) {
      if (!isCurrent()) return;
      syncNotesRef.current = false;
      setHeaderError(e instanceof Error ? e.message : "Unknown error");
    } finally {
      if (isCurrent()) setHeaderLoading(false);
    }
  }, [scope, entityId, departmentId, reportTagId, canLoad]);

  const loadBody = React.useCallback(async (): Promise<void> => {
    if (!canLoad || !requestedLayout || correcting) return;
    const requestId = ++latestBodyRequestRef.current;
    const { signal } = nextAbortController(bodyAbortRef);
    const isCurrent = () => !signal.aborted && requestId === latestBodyRequestRef.current;
    setBodyLoading(true);
    setBodyError(null);
    try {
      const tab = activeReportTab(scope, searchParams);
      const keys = queryKeysForTab(scope, tab);
      const result = await fetchReportBodyAsync(
        {
          ...scopeIdPart(scope, entityId),
          departmentId,
          reportTagId,
          perPage,
          origin: originForTab(tab),
          pageParam: { key: keys.page, value: Number(searchParams.get(keys.page)) || 1 },
          lensParams: collectLensApiParams(searchParams, lensKeyForTab(scope, tab), scope),
          layout: requestedLayout,
        } as Parameters<typeof fetchReportBodyAsync>[0],
        signal
      );
      if (!isCurrent()) return;
      if (result.isSuccess()) {
        setResp(result.getData());
        const correction = layoutParamCorrection(
          searchParams.get(keys.layout),
          result.getData()?.data?.current_layout
        );
        if (correction) {
          preloadedBodyKeyRef.current = reportBodyKey(reportKey, withLayout(searchParams, correction, scope), scope);
          setLayout(correction, true);
        }
      } else {
        setBodyError(result.getError()?.detail || "Failed to fetch report");
      }
    } catch (e) {
      if (!isCurrent()) return;
      setBodyError(e instanceof Error ? e.message : "Unknown error");
    } finally {
      if (isCurrent()) setBodyLoading(false);
    }
    // bodyKey covers every searchParams value the request reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyKey, canLoad, correcting, setLayout]);

  /** Status actions change both the report details and the table, so both reload. */
  const load = React.useCallback(async (): Promise<void> => {
    await Promise.all([loadHeader(), loadBody()]);
  }, [loadHeader, loadBody]);

  /**
   * A different report starts clean: nothing from the previous report (details, table, notes, errors) stays on
   * screen, and its notes are taken from the first header that loads. Within one report a failed reload keeps the
   * last good data and only sets the error.
   */
  React.useEffect(() => {
    syncNotesRef.current = true;
    preloadedBodyKeyRef.current = null;
    setDeptNotes("");
    setHeader(null);
    setResp(null);
    setHeaderError(null);
    setBodyError(null);
  }, [reportKey]);

  React.useEffect(() => {
    if (canLoad) void loadHeader();
  }, [canLoad, loadHeader]);

  React.useEffect(() => {
    if (!canLoad || correcting) return;
    if (preloadedBodyKeyRef.current === bodyKey) {
      preloadedBodyKeyRef.current = null;
      return;
    }
    preloadedBodyKeyRef.current = null;
    void loadBody();
  }, [canLoad, correcting, bodyKey, loadBody]);

  const loadDepartmentReport = React.useCallback(async (): Promise<void> => {
    if (entityId <= 0 || departmentId <= 0) {
      setDeptReport(null);
      return;
    }
    setDeptReportLoading(true);
    try {
      const result = await fetchDepartmentReportsQueryAsync({
        ...scopeIdPart(scope, entityId),
        departmentId,
        reportTagId: reportTagId || undefined,
        page: 1,
        perPage: 50,
      } as Parameters<typeof fetchDepartmentReportsQueryAsync>[0]);
      if (!result.isSuccess()) {
        setDeptReport(null);
        return;
      }
      const items = result.getData()?.data ?? [];
      setDeptReport(items.find((x) => x.department_id === departmentId) ?? null);
    } finally {
      setDeptReportLoading(false);
    }
  }, [scope, entityId, departmentId, reportTagId]);

  React.useEffect(() => {
    if (canLoad) loadDepartmentReport();
  }, [canLoad, loadDepartmentReport]);

  /**
   * Latest loaders for code that runs after an await. A handler's own closure holds the loaders from the render it
   * started in; after a Layout or View change meanwhile, those would cancel the newer request and load the old URL.
   */
  const loadRef = React.useRef(load);
  const loadHeaderRef = React.useRef(loadHeader);
  const loadBodyRef = React.useRef(loadBody);
  const loadDepartmentReportRef = React.useRef(loadDepartmentReport);
  React.useLayoutEffect(() => {
    loadRef.current = load;
    loadHeaderRef.current = loadHeader;
    loadBodyRef.current = loadBody;
    loadDepartmentReportRef.current = loadDepartmentReport;
  });
  const reload = React.useCallback(() => loadRef.current(), []);
  const reloadHeader = React.useCallback(() => loadHeaderRef.current(), []);
  const reloadBody = React.useCallback(() => loadBodyRef.current(), []);
  const reloadDepartmentReport = React.useCallback(() => loadDepartmentReportRef.current(), []);

  const meta = header?.meta;
  const submission = (meta as any)?.department_submission ?? null;
  const submissionSummary: SubmissionSummary | null =
    (meta as { submission_summary?: SubmissionSummary | null } | undefined)
      ?.submission_summary ?? null;
  const formSections = React.useMemo(() => resp?.data?.sections ?? [], [resp]);
  const officeSection = formSections.find((s) => s.type === "office") ?? null;
  const childrenSection = formSections.find((s) => s.type === "children") ?? null;
  const { start: resultStart, end: resultEnd } = getPaginationRange({
    page: resp?.current_page ?? page,
    perPage: resp?.per_page ?? perPage,
    total: resp?.total ?? 0,
    currentCount: resp?.data?.rows?.length ?? 0,
  });

  const departmentReportId: number | null =
    submission?.department_report_id ?? deptReport?.department_report_id ?? null;

  const isMissingReport = isDepartmentReportMissing(departmentReportId);

  const statusName = resolveDepartmentReportDisplayStatus(
    departmentReportId,
    submission?.report_status_name ?? deptReport?.department_report_status_name ?? null
  );
  const processResults = (meta as { process_results?: ReportProcessResults | null } | undefined)
    ?.process_results;
  const needsAttentionReason = getNeedsAttentionReason(statusName, processResults);
  const needsAttentionDetails = parseNeedsAttentionDetails(processResults);
  const approvalNotesDisplay = getApprovalNotes(
    submission?.approval_notes,
    processResults
  );
  const notesEditable = isDepartmentReportEditable(statusName);
  const currentUserId = useAuthStore((s) => s.getAuthUser()?.id ?? null);
  const isOwnReport = isCurrentUserSubmitter(currentUserId, submission?.submitted_by);

  const selectedTag = tags.find((t) => t.report_tag_id === reportTagId);
  const tagStatusName = selectedTag?.report_tag_status_name ?? null;
  // Unknown/loading → allow UI writes (same as former isTagClosed-only gate); known non-OPEN → block.
  const tagIsOpen =
    tagStatusName == null || tagStatusName === ""
      ? true
      : isTagOpen(tagStatusName);
  const tagIsClosed = isTagClosed(tagStatusName);
  const writesAllowedByTag = tagIsOpen;
  const actionsLocked = (meta as { actions_locked?: boolean } | null)?.actions_locked === true;

  const {
    loading: permissionsLoading,
    canSave,
    canSubmit,
    canReview,
    canApprove,
    canApproveOwn,
    canValidate,
    canUploadMedia,
    canRemoveMedia,
    canReviewActivity,
  } = useReportActionPermissions({
    scope,
    scopeId: entityId,
    departmentId,
    includeActivityEntry: true,
    enabled: entityId > 0 && departmentId > 0,
  });

  const permissionFlags: ReportPermissionFlags = React.useMemo(
    () =>
      buildReportPermissionFlags(
        notesEditable && writesAllowedByTag,
        {
          canSave,
          canSubmit,
          canReview,
          canApprove,
          canApproveOwn,
          canValidate,
          canUploadMedia,
          canRemoveMedia,
        },
        {
          canValidateByWorkflow: canValidateDepartmentReport(statusName),
          canApproveByWorkflow: canApproveDepartmentReport(statusName),
          canManualValidateByWorkflow:
            normalizeReportWorkflowStatus(statusName) === "SUBMITTED",
          isOwnReport,
          actionsLocked,
        }
      ),
    [
      notesEditable,
      writesAllowedByTag,
      canSave,
      canSubmit,
      canReview,
      canApprove,
      canApproveOwn,
      canValidate,
      canUploadMedia,
      canRemoveMedia,
      statusName,
      isOwnReport,
      actionsLocked,
    ]
  );

  const showPaging = Boolean(
    formSections.length === 0 &&
      resp &&
      (typeof resp.total === "number"
        ? resp.total > perPage
        : !!(resp.next_page_url || resp.prev_page_url))
  );

  const existingMedia = React.useMemo<DepartmentReportMediaItem[]>(
    () => (Array.isArray((meta as any)?.media) ? ((meta as any).media as DepartmentReportMediaItem[]) : []),
    [meta]
  );
  const mediaRequirements = React.useMemo(
    () =>
      Array.isArray((meta as any)?.documents)
        ? ((meta as any).documents as DepartmentReportDocument[])
        : [],
    [meta]
  );
  const media = useDepartmentReportMedia({
    scope,
    entityId,
    departmentId,
    reportTagId,
    notes: deptNotes,
    departmentReportId,
    existingMedia,
    editable: permissionFlags.fieldsEditable && canUploadMedia,
    onChanged: async () => {
      await reloadHeader();
      await reloadDepartmentReport();
    },
  });

  React.useEffect(() => {
    setDeptActionError(null);
    setValidationResult(null);
    media.clearUploadDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, departmentId, reportTagId]);

  React.useEffect(() => {
    if (!showApproveModal) {
      setApproveNotes("");
      setApproveError(null);
    }
  }, [showApproveModal]);

  React.useEffect(() => {
    if (!showReviewModal) {
      setReviewError(null);
    }
  }, [showReviewModal]);

  const columnsSpec = React.useMemo(() => toTreeColumnsSpec(resp?.data?.columns ?? []), [resp]);
  const treeData = React.useMemo(() => buildTreeByScope(scope, resp?.data?.rows ?? []), [scope, resp]);

  const sectionViews = React.useMemo(() => {
    return formSections.map((section) => {
      const rows = "rows" in section ? section.rows ?? [] : [];
      const columns = "columns" in section ? section.columns ?? [] : [];
      return {
        section,
        columnsSpec: toTreeColumnsSpec(columns),
        tree: buildTreeForSection(section.type, scope, rows),
        totals: "totals" in section ? section.totals ?? null : null,
        currentPage: "current_page" in section ? section.current_page ?? 1 : 1,
        lastPage: "last_page" in section ? section.last_page ?? 1 : 1,
        perPage: "per_page" in section ? section.per_page ?? perPage : perPage,
        total: "total" in section ? section.total ?? rows.length : rows.length,
      };
    });
  }, [formSections, scope, perPage]);

  // TODO: Department report PDF download is turned off until it is rebuilt on the report header and body endpoints.

  const handleSaveDraft = React.useCallback(async () => {
    if (!canLoad) return;
    setDeptActionError(null);
    setSavingDraft(true);
    try {
      media.clearUploadDraft();
      const result = await requestDepartmentReportSaveAsync({
        ...scopeIdPart(scope, entityId),
        departmentId,
        reportTagId,
        notes: deptNotes,
        existingMedia: media.captionPatches,
      });
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to save draft.");
      syncNotesRef.current = true;
      media.resyncCaptionsFromServer();
      await reload();
      await reloadDepartmentReport();
    } catch (e) {
      surfaceDeptActionError(
        e instanceof Error ? e.message : "Failed to save draft.",
        setDeptActionError
      );
    } finally {
      setSavingDraft(false);
    }
  }, [scope, entityId, departmentId, reportTagId, deptNotes, media, canLoad, reload, reloadDepartmentReport]);

  const handleSubmitDepartmentReport = React.useCallback(async () => {
    if (!canLoad) return;
    setDeptActionError(null);
    setSubmittingDept(true);
    try {
      media.clearUploadDraft();
      const result = await requestDepartmentReportSubmitAsync({
        ...scopeIdPart(scope, entityId),
        departmentId,
        reportTagId,
        notes: deptNotes,
        existingMedia: media.captionPatches,
      });
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to submit report.");
      syncNotesRef.current = true;
      media.resyncCaptionsFromServer();
      await reload();
      await reloadDepartmentReport();
    } catch (e) {
      surfaceDeptActionError(
        e instanceof Error ? e.message : "Failed to submit report.",
        setDeptActionError
      );
    } finally {
      setSubmittingDept(false);
    }
  }, [scope, entityId, departmentId, reportTagId, deptNotes, media, canLoad, reload, reloadDepartmentReport]);

  const handleValidateDepartmentReport = React.useCallback(async () => {
    if (!canLoad) return;
    setDeptActionError(null);
    setValidatingDept(true);
    try {
      const result = await processDepartmentReportAsync({
        ...scopeIdPart(scope, entityId),
        departmentId,
        reportTagId,
      });
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to validate report.");
      setValidationResult(result.getData());
      await reload();
      await reloadDepartmentReport();
    } catch (e) {
      surfaceDeptActionError(
        e instanceof Error ? e.message : "Failed to validate report.",
        setDeptActionError
      );
    } finally {
      setValidatingDept(false);
    }
  }, [scope, entityId, departmentId, reportTagId, canLoad, reload, reloadDepartmentReport]);

  const handleApprove = React.useCallback(async () => {
    if (!canLoad) return;
    if (!departmentReportId) {
      setApproveError("Missing department report id. Cannot approve report.");
      return;
    }
    setApproving(true);
    setApproveError(null);
    try {
      const result = await requestReportApproveAsync({
        ...scopeIdPart(scope, entityId),
        departmentId,
        departmentReportId: Number(departmentReportId),
        notes: approveNotes ?? "",
      });
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to approve report.");
      setShowApproveModal(false);
      await reload();
      await reloadDepartmentReport();
    } catch (e) {
      setApproveError(e instanceof Error ? e.message : "Failed to approve report.");
    } finally {
      setApproving(false);
    }
  }, [scope, entityId, departmentId, departmentReportId, approveNotes, canLoad, reload, reloadDepartmentReport]);

  const handleRequestReview = React.useCallback(
    async ({ reason }: { reason: string }) => {
      if (!canLoad) return;
      if (!departmentReportId) {
        setReviewError("Missing department report id. Cannot request review.");
        return;
      }
      setRequestingReview(true);
      setReviewError(null);
      try {
        const result = await requestReportReviewAsync({
          ...scopeIdPart(scope, entityId),
          departmentId,
          departmentReportId: Number(departmentReportId),
          notes: reason.slice(0, 255),
        });
        if (!result.isSuccess()) {
          throw new Error(result.getError()?.detail || "Failed to request review.");
        }
        setShowReviewModal(false);
        await reload();
        await reloadDepartmentReport();
      } catch (e) {
        setReviewError(e instanceof Error ? e.message : "Failed to request review.");
      } finally {
        setRequestingReview(false);
      }
    },
    [scope, entityId, departmentId, departmentReportId, canLoad, reload, reloadDepartmentReport]
  );

  const setSectionLens = React.useCallback(
    (section: SectionLensKey, lens: ReportSectionViewLensParams) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        writeSectionLens(
          next,
          section,
          {
            view: parseReportSectionView(lens.view),
            bucket: lens.bucket,
            from: lens.from,
            to: lens.to,
          },
          scope
        );
        return next;
      });
    },
    [scope, setSearchParams]
  );

  const allowedSectionTypes = React.useMemo(() => {
    const meta = header?.section_view_meta ?? {};
    return reportTabsForScope(scope).filter((tab) => meta[lensKeyForTab(scope, tab)] != null);
  }, [header?.section_view_meta, scope]);

  const activeSection = readActiveReportSection(searchParams, allowedSectionTypes);

  const setActiveSection = React.useCallback(
    (sectionType: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        writeActiveReportSection(next, sectionType, allowedSectionTypes);
        return next;
      });
    },
    [allowedSectionTypes, setSearchParams]
  );

  return {
    // identifiers
    scope,
    entityId,
    departmentId,
    reportTagId,
    // data
    resp,
    loading,
    actionsLoading,
    headerLoading,
    bodyLoading,
    error,
    assignmentSnapshotWarning: (meta?.assignment_snapshot_warning as string | null | undefined) ?? null,
    meta,
    submission,
    submissionSummary,
    officeSection,
    childrenSection,
    sections: formSections,
    sectionViews,
    statusName,
    needsAttentionReason,
    needsAttentionDetails,
    approvalNotesDisplay,
    departmentReportId,
    isMissingReport,
    canLoad,
    showPaging,
    notesEditable,
    permissionFlags,
    permissionsLoading,
    canReviewActivity,
    selectedTag,
    tagStatusName,
    tagIsOpen,
    tagIsClosed,
    writesAllowedByTag,
    actionsLocked,
    columnsSpec,
    treeData,
    existingMedia,
    mediaRequirements,
    media,
    tags,
    tagsLoading,
    // pagination
    page,
    perPage,
    onPageChange,
    resultStart,
    resultEnd,
    // notes + actions
    deptNotes,
    setDeptNotes,
    savingDraft,
    submittingDept,
    validatingDept,
    deptActionError,
    validationResult,
    deptReportLoading,
    load: reload,
    loadHeader: reloadHeader,
    loadBody: reloadBody,
    loadDepartmentReport: reloadDepartmentReport,
    handleSaveDraft,
    handleSubmitDepartmentReport,
    handleValidateDepartmentReport,
    // approve modal
    showApproveModal,
    setShowApproveModal,
    approveNotes,
    setApproveNotes,
    approving,
    approveError,
    handleApprove,
    // request review modal
    showReviewModal,
    setShowReviewModal,
    requestingReview,
    reviewError,
    setReviewError,
    handleRequestReview,
    setSectionLens,
    activeSection,
    setActiveSection,
    searchParams,
    // presentation layouts
    header,
    sectionViewMeta: header?.section_view_meta ?? {},
    availableLayouts: header?.available_layouts ?? [],
    currentLayout: resp?.data?.current_layout ?? LAYOUT_DEFAULT,
    requestedLayout,
    renderedLayout: resp?.data?.layout ?? null,
    onLayoutChange: setLayout,
  };
}

export type UseReportPageResult = ReturnType<typeof useReportPage>;
